import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  Layers, Plus, Sparkles, BookOpen, Languages, Trash2, Play, Download,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { ROUTES } from "@/constants/routes"
import {
  fetchDecks, fetchTodayStats, createDeck, deleteDeck,
  generateFromKnowledgePoints, generateFromMistakes, generateVocab,
  fetchFlashcardTemplates, fetchCards,
  type DeckListItem, type TodayStats, type FlashcardTemplate,
} from "@/api/flashcards"
import { downloadDeckHTML } from "@/components/flashcards/exportDeckHTML"
import { MICRO_THEMES } from "@ai-tutor/shared"
import { fetchKnowledgePoints, type KnowledgePoint } from "@/api/knowledgePoints"
import { fetchMistakes, type MistakeRecordListItem } from "@/api/mistakes"
import { fetchExamPapers, type ExamPaperListItem } from "@/api/exam"

const SOURCE_LABELS: Record<string, string> = {
  knowledge: "知识点生成",
  mistake: "错题生成",
  vocab: "词汇卡",
  imported: "线下导入",
  manual: "手动创建",
}

// 模板列表模块级缓存，避免多个弹窗重复请求
let templatesCache: FlashcardTemplate[] | null = null

function useTemplates() {
  const [templates, setTemplates] = useState<FlashcardTemplate[]>(templatesCache ?? [])
  useEffect(() => {
    if (templatesCache) return
    fetchFlashcardTemplates()
      .then((t) => {
        templatesCache = t
        setTemplates(t)
      })
      .catch(() => {})
  }, [])
  return templates
}

function TemplateSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const templates = useTemplates()
  const current = templates.find((t) => t.id === value)
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-600 shrink-0">卡片模板</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-48 h-9">
          <SelectValue placeholder="选择模板" />
        </SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {current && <span className="text-xs text-slate-400 truncate flex-1">{current.description}</span>}
    </div>
  )
}

export default function FlashcardDeckListPage() {
  const navigate = useNavigate()
  const [decks, setDecks] = useState<DeckListItem[]>([])
  const [stats, setStats] = useState<TodayStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showNewDeck, setShowNewDeck] = useState(false)
  const [newDeckName, setNewDeckName] = useState("")
  const [creating, setCreating] = useState(false)

  const [genType, setGenType] = useState<"knowledge" | "mistake" | "vocab" | null>(null)
  const [exportDeck, setExportDeck] = useState<DeckListItem | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    Promise.all([fetchDecks(), fetchTodayStats()])
      .then(([d, s]) => {
        setDecks(d)
        setStats(s)
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function handleCreateDeck() {
    if (!newDeckName.trim()) return
    setCreating(true)
    try {
      await createDeck({ name: newDeckName.trim() })
      toast.success("卡组已创建")
      setShowNewDeck(false)
      setNewDeckName("")
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "创建失败")
    } finally {
      setCreating(false)
    }
  }

  async function handleDeleteDeck(deck: DeckListItem) {
    if (!window.confirm(`确定删除卡组「${deck.name}」？`)) return
    try {
      await deleteDeck(deck.id)
      toast.success("卡组已删除")
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "删除失败")
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Layers className="w-6 h-6" />
              闪卡
            </h1>
            <p className="text-sm text-slate-500">间隔重复复习，让记忆更牢固</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowNewDeck(true)}>
              <Plus className="w-4 h-4 mr-2" />
              新建卡组
            </Button>
            {(stats?.totalDue ?? 0) > 0 && (
              <Button onClick={() => navigate(ROUTES.FLASHCARD_STUDY)}>
                <Play className="w-4 h-4 mr-2" />
                开始复习（{stats!.totalDue}）
              </Button>
            )}
          </div>
        </div>

        {/* 今日到期统计 */}
        {stats && (
          <Card>
            <CardContent className="py-4 flex items-center gap-6 flex-wrap">
              <div>
                <div className="text-2xl font-bold">{stats.totalDue}</div>
                <div className="text-xs text-slate-500">今日待复习</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{decks.length}</div>
                <div className="text-xs text-slate-500">卡组总数</div>
              </div>
              {stats.decks.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {stats.decks.map((d) => (
                    <button
                      key={d.deckId}
                      onClick={() => navigate(`${ROUTES.FLASHCARD_STUDY}?deckId=${d.deckId}`)}
                      className="text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      {d.deckName} · {d.dueCount}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 生成入口 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button onClick={() => setGenType("knowledge")} className="text-left">
            <Card className="h-full hover:shadow-md transition-shadow border-blue-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                  <Sparkles className="w-4 h-4" />
                  从知识点生成
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500">AI 根据知识点定义与常见错误自动生成概念卡</p>
              </CardContent>
            </Card>
          </button>
          <button onClick={() => setGenType("mistake")} className="text-left">
            <Card className="h-full hover:shadow-md transition-shadow border-rose-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-rose-700">
                  <BookOpen className="w-4 h-4" />
                  从错题生成
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500">AI 把错题提炼成复盘记忆卡</p>
              </CardContent>
            </Card>
          </button>
          <button onClick={() => setGenType("vocab")} className="text-left">
            <Card className="h-full hover:shadow-md transition-shadow border-emerald-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
                  <Languages className="w-4 h-4" />
                  英语词汇卡
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500">从英语试卷中自动抽取生词</p>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* 卡组列表 */}
        {loading && <div className="text-sm text-slate-500">加载中...</div>}
        {error && <div className="text-sm text-red-500">{error}</div>}
        {!loading && !error && decks.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-slate-500">
              还没有卡组，试试上面的生成功能或手动新建
            </CardContent>
          </Card>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!loading && decks.map((deck) => (
            <Card key={deck.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">
                      <Link to={`/flashcards/deck/${deck.id}`} className="hover:text-blue-600">
                        {deck.name}
                      </Link>
                    </CardTitle>
                    <CardDescription>
                      {SOURCE_LABELS[deck.source] ?? deck.source}
                      {deck.subject ? ` · ${deck.subject}` : ""}
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-500"
                    onClick={() => handleDeleteDeck(deck)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  {deck.cardCount} 张卡片
                  {deck.dueCount > 0 && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      {deck.dueCount} 待复习
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setExportDeck(deck)}
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    导出
                  </Button>
                  {deck.dueCount > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`${ROUTES.FLASHCARD_STUDY}?deckId=${deck.id}`)}
                    >
                      复习
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* 新建卡组弹窗 */}
      <Dialog open={showNewDeck} onOpenChange={setShowNewDeck}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建卡组</DialogTitle>
            <DialogDescription>创建一个空白卡组，之后可以手动添加卡片</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="卡组名称"
            value={newDeckName}
            onChange={(e) => setNewDeckName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateDeck()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDeck(false)}>取消</Button>
            <Button onClick={handleCreateDeck} disabled={creating || !newDeckName.trim()}>
              {creating ? "创建中..." : "创建"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 生成弹窗 */}
      {genType === "knowledge" && (
        <KnowledgeGenerateDialog onClose={() => setGenType(null)} onDone={load} />
      )}
      {genType === "mistake" && (
        <MistakeGenerateDialog onClose={() => setGenType(null)} onDone={load} />
      )}
      {genType === "vocab" && (
        <VocabGenerateDialog onClose={() => setGenType(null)} onDone={load} />
      )}

      {/* 导出弹窗 */}
      {exportDeck && (
        <ExportDeckDialog deck={exportDeck} onClose={() => setExportDeck(null)} />
      )}
    </div>
  )
}

// ─── 导出为 reveal.js 课件 ─────────────────────────────────

const BUILTIN_THEMES = MICRO_THEMES.filter((t) => t.type === "builtin")

function ExportDeckDialog({ deck, onClose }: { deck: DeckListItem; onClose: () => void }) {
  const [theme, setTheme] = useState("white")
  const [exporting, setExporting] = useState(false)

  async function handleExport() {
    setExporting(true)
    try {
      const res = await fetchCards({ deckId: deck.id, pageSize: 500 })
      if (res.items.length === 0) {
        toast.error("卡组暂无卡片")
        return
      }
      downloadDeckHTML(deck.name, res.items, { theme, subject: deck.subject })
      toast.success("已导出 reveal.js 课件")
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "导出失败")
    } finally {
      setExporting(false)
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>导出为课件</DialogTitle>
          <DialogDescription>
            生成可独立运行的 reveal.js HTML 文件，每张卡一页，按键逐张揭示答案，适合课堂投影或离线复习
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600 shrink-0">主题</span>
          <Select value={theme} onValueChange={setTheme}>
            <SelectTrigger className="w-40 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BUILTIN_THEMES.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "导出中..." : "下载 HTML"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── 从知识点生成 ─────────────────────────────────────────────────

function KnowledgeGenerateDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [kps, setKps] = useState<KnowledgePoint[]>([])
  const [loading, setLoading] = useState(true)
  const [keyword, setKeyword] = useState("")
  const [selected, setSelected] = useState<string[]>([])
  const [generating, setGenerating] = useState(false)
  const [template, setTemplate] = useState("qa")

  useEffect(() => {
    fetchKnowledgePoints({})
      .then((rows) => setKps(rows.filter((k) => k.level !== 0)))
      .catch(() => setKps([]))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase()
    if (!kw) return kps.slice(0, 200)
    return kps.filter((k) => k.name.toLowerCase().includes(kw) || k.kp_code.toLowerCase().includes(kw)).slice(0, 200)
  }, [kps, keyword])

  function toggle(code: string) {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : prev.length >= 20 ? prev : [...prev, code],
    )
  }

  async function handleGenerate() {
    if (selected.length === 0) return
    setGenerating(true)
    try {
      const res = await generateFromKnowledgePoints({ kpCodes: selected, template })
      toast.success(`生成完成：新增 ${res.created} 张，跳过 ${res.skipped} 张`)
      onDone()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "生成失败")
      setGenerating(false)
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>从知识点生成闪卡</DialogTitle>
          <DialogDescription>选择最多 20 个知识点，按所选模板自动生成卡片</DialogDescription>
        </DialogHeader>
        <TemplateSelect value={template} onChange={setTemplate} />
        <Input placeholder="搜索知识点..." value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
          {loading && <div className="p-4 text-sm text-slate-500">加载中...</div>}
          {!loading && filtered.length === 0 && <div className="p-4 text-sm text-slate-500">没有匹配的知识点</div>}
          {filtered.map((kp) => (
            <label key={kp.kp_code} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50">
              <Checkbox checked={selected.includes(kp.kp_code)} onCheckedChange={() => toggle(kp.kp_code)} />
              <span className="text-sm">{kp.name}</span>
              <span className="text-xs text-slate-400 ml-auto">{kp.subject ?? ""}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <span className="text-xs text-slate-500 mr-auto">已选 {selected.length}/20</span>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleGenerate} disabled={generating || selected.length === 0}>
            {generating ? "AI 生成中..." : "生成"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── 从错题生成 ───────────────────────────────────────────────────

function MistakeGenerateDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [items, setItems] = useState<MistakeRecordListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<string[]>([])
  const [generating, setGenerating] = useState(false)
  const [template, setTemplate] = useState("qa")

  useEffect(() => {
    fetchMistakes({ page: 1, pageSize: 50, includeMastered: false })
      .then((res) => setItems(res.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 20 ? prev : [...prev, id],
    )
  }

  async function handleGenerate() {
    if (selected.length === 0) return
    setGenerating(true)
    try {
      const res = await generateFromMistakes({ mistakeRecordIds: selected, template })
      toast.success(`生成完成：新增 ${res.created} 张，跳过 ${res.skipped} 张`)
      onDone()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "生成失败")
      setGenerating(false)
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>从错题生成闪卡</DialogTitle>
          <DialogDescription>选择最多 20 道错题，按所选模板提炼成记忆卡</DialogDescription>
        </DialogHeader>
        <TemplateSelect value={template} onChange={setTemplate} />
        <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
          {loading && <div className="p-4 text-sm text-slate-500">加载中...</div>}
          {!loading && items.length === 0 && <div className="p-4 text-sm text-slate-500">暂无错题记录</div>}
          {items.map((m) => (
            <label key={m.id} className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50">
              <Checkbox className="mt-1" checked={selected.includes(m.id)} onCheckedChange={() => toggle(m.id)} />
              <span className="text-sm line-clamp-2">{m.questionStem || "（无题干摘要）"}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <span className="text-xs text-slate-500 mr-auto">已选 {selected.length}/20</span>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleGenerate} disabled={generating || selected.length === 0}>
            {generating ? "AI 生成中..." : "生成"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── 词汇卡生成 ───────────────────────────────────────────────────

function VocabGenerateDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [papers, setPapers] = useState<ExamPaperListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [paperId, setPaperId] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [template, setTemplate] = useState("vocab")

  useEffect(() => {
    fetchExamPapers()
      .then((rows) => setPapers(rows.filter((p) => p.subject === "英语" || !p.subject)))
      .catch(() => setPapers([]))
      .finally(() => setLoading(false))
  }, [])

  async function handleGenerate() {
    if (!paperId) return
    setGenerating(true)
    try {
      const res = await generateVocab({ paperId, template })
      toast.success(`生成完成：新增 ${res.created} 张，跳过 ${res.skipped} 张`)
      onDone()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "生成失败")
      setGenerating(false)
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>生成英语词汇卡</DialogTitle>
          <DialogDescription>选择一份英语试卷，按所选模板抽取值得积累的词汇</DialogDescription>
        </DialogHeader>
        <TemplateSelect value={template} onChange={setTemplate} />
        <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
          {loading && <div className="p-4 text-sm text-slate-500">加载中...</div>}
          {!loading && papers.length === 0 && <div className="p-4 text-sm text-slate-500">暂无英语试卷</div>}
          {papers.map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50">
              <input
                type="radio"
                name="vocab-paper"
                checked={paperId === p.id}
                onChange={() => setPaperId(p.id)}
              />
              <span className="text-sm">{p.name}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleGenerate} disabled={generating || !paperId}>
            {generating ? "AI 生成中..." : "生成"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
